import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import {
  updateGovernmentRegistrantAccountAccountsContact,
  updateOperatorRegistrantAccountAccountsContact,
  updateRegistrantAccountLoginIdentityAccountsContact,
  updateRegistrantAccountUserAccountsContact,
  updateRegistrantLoginIdentityGovernment,
  updateRegistrantLoginIdentityOperator,
} from "@/hera-api/client";
import type {
  PreLoginAccountsContactBriefSchema,
  RegistrantLoginIdentityBriefSchema,
} from "@/hera-api/client/types.gen";
import { invalidateRegistrantUserQueries } from "@/lib/registrant-user-query-invalidation";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

export type AccountsContactPortal = "operator" | "government" | "registrant";

export interface AccountsContactTogglesProps {
  loginIdentities: RegistrantLoginIdentityBriefSchema[];
  /** Org members who do not have a portal login yet (invite pending); toggled via account-level API. */
  preLoginAccountsContact?: PreLoginAccountsContactBriefSchema[];
  portal: AccountsContactPortal;
  /** When false, switches are read-only (e.g. secondary registrant account). */
  disabled?: boolean;
  /** Compact enable/disable switch for the registrant users table. */
  compact?: boolean;
  idPrefix?: string;
  onLoginIdentityChange?: (loginIdentityId: string, checked: boolean) => void;
  onPreLoginChange?: (registrantAccountId: string, checked: boolean) => void;
}

export function AccountsContactToggles({
  loginIdentities,
  preLoginAccountsContact = [],
  portal,
  disabled = false,
  compact = false,
  idPrefix = "",
  onLoginIdentityChange,
  onPreLoginChange,
}: AccountsContactTogglesProps) {
  const queryClient = useQueryClient();
  const [optimisticContact, setOptimisticContact] = useState<
    Record<string, boolean>
  >({});

  const contactOn = (id: string, serverValue: boolean) =>
    optimisticContact[id] ?? serverValue;

  const setContactOn = (id: string, value: boolean) => {
    setOptimisticContact((prev) => ({ ...prev, [id]: value }));
  };

  const clearContactOn = (id: string) => {
    setOptimisticContact((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const deferPersist = Boolean(onLoginIdentityChange || onPreLoginChange);
  const preLoginKey = (registrantAccountId: string) =>
    `pre-${registrantAccountId}`;

  const mutation = useMutation({
    mutationFn: async (
      payload:
        | { mode: "login_identity"; loginIdentityId: string; value: boolean }
        | { mode: "pre_login"; registrantAccountId: string; value: boolean },
    ) => {
      const body = { is_accounts_contact: payload.value };
      if (payload.mode === "login_identity") {
        if (portal === "operator") {
          const res = await updateRegistrantLoginIdentityOperator({
            path: { login_identity_id: payload.loginIdentityId },
            body,
          });
          if (res.error) {
            throw new Error(getQueryErrorMessage(res.error));
          }
          return;
        }
        if (portal === "government") {
          const res = await updateRegistrantLoginIdentityGovernment({
            path: { login_identity_id: payload.loginIdentityId },
            body,
          });
          if (res.error) {
            throw new Error(getQueryErrorMessage(res.error));
          }
          return;
        }
        const res = await updateRegistrantAccountLoginIdentityAccountsContact({
          path: { login_identity_id: payload.loginIdentityId },
          body,
        });
        if (res.error) {
          throw new Error(getQueryErrorMessage(res.error));
        }
        return;
      }
      if (portal === "operator") {
        const res = await updateOperatorRegistrantAccountAccountsContact({
          path: { registrant_account_id: payload.registrantAccountId },
          body,
        });
        if (res.error) {
          throw new Error(getQueryErrorMessage(res.error));
        }
        return;
      }
      if (portal === "government") {
        const res = await updateGovernmentRegistrantAccountAccountsContact({
          path: { registrant_account_id: payload.registrantAccountId },
          body,
        });
        if (res.error) {
          throw new Error(getQueryErrorMessage(res.error));
        }
        return;
      }
      const res = await updateRegistrantAccountUserAccountsContact({
        path: { user_id: payload.registrantAccountId },
        body,
      });
      if (res.error) {
        throw new Error(getQueryErrorMessage(res.error));
      }
    },
    onSuccess: () => {
      invalidateRegistrantUserQueries(queryClient);
      void queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          (q.queryKey[0] as string) ===
            "getNonRegistrantRegistrantAccountLoginIdentities",
      });
      toast({
        title: "Saved",
        description: "Accounts contact setting updated.",
      });
    },
    onError: (
      err: Error,
      payload:
        | { mode: "login_identity"; loginIdentityId: string; value: boolean }
        | { mode: "pre_login"; registrantAccountId: string; value: boolean },
    ) => {
      clearContactOn(
        payload.mode === "login_identity"
          ? payload.loginIdentityId
          : preLoginKey(payload.registrantAccountId),
      );
      toast({
        title: "Error",
        description: err.message || "Could not update accounts contact",
        variant: "destructive",
      });
    },
  });

  const onCheckedChangeLi = useCallback(
    (loginIdentityId: string, checked: boolean) => {
      mutation.mutate({
        mode: "login_identity",
        loginIdentityId,
        value: checked,
      });
    },
    [mutation],
  );

  const onCheckedChangePreLogin = useCallback(
    (registrantAccountId: string, checked: boolean) => {
      mutation.mutate({
        mode: "pre_login",
        registrantAccountId,
        value: checked,
      });
    },
    [mutation],
  );

  if (loginIdentities.length === 0 && preLoginAccountsContact.length === 0) {
    return (
      <span className="text-xs text-muted-foreground">
        {compact ? "—" : "No portal logins yet"}
      </span>
    );
  }

  if (compact) {
    return (
      <div
        className="flex flex-col gap-2 py-1 text-sm whitespace-nowrap"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {loginIdentities.map((li) => {
          const isOn = contactOn(li.id, li.is_accounts_contact);
          return (
            <div
              key={li.id}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              {loginIdentities.length > 1 ? (
                <span className="text-xs truncate max-w-[8rem]">
                  {li.email}
                </span>
              ) : null}
              <Switch
                id={`${idPrefix}acct-contact-${li.id}`}
                checked={isOn}
                disabled={disabled || (!deferPersist && mutation.isPending)}
                onCheckedChange={(checked) => {
                  setContactOn(li.id, checked);
                  if (onLoginIdentityChange) {
                    onLoginIdentityChange(li.id, checked);
                    return;
                  }
                  onCheckedChangeLi(li.id, checked);
                }}
                aria-label={`Accounts contact for ${li.email}`}
              />
              <Label
                htmlFor={`${idPrefix}acct-contact-${li.id}`}
                className="font-normal shrink-0"
              >
                {isOn ? "Enabled" : "Disabled"}
              </Label>
            </div>
          );
        })}
        {preLoginAccountsContact.map((row) => {
          const key = preLoginKey(row.registrant_account_id);
          const isOn = contactOn(key, row.accounts_contact_before_login);
          return (
            <div
              key={row.registrant_account_id}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <Switch
                id={`${idPrefix}acct-contact-pre-${row.registrant_account_id}`}
                checked={isOn}
                disabled={disabled || (!deferPersist && mutation.isPending)}
                onCheckedChange={(checked) => {
                  setContactOn(key, checked);
                  if (onPreLoginChange) {
                    onPreLoginChange(row.registrant_account_id, checked);
                    return;
                  }
                  onCheckedChangePreLogin(row.registrant_account_id, checked);
                }}
                aria-label={`Accounts contact (before login) for ${row.email}`}
              />
              <Label
                htmlFor={`${idPrefix}acct-contact-pre-${row.registrant_account_id}`}
                className="font-normal shrink-0"
              >
                {isOn ? "Enabled" : "Disabled"}
              </Label>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 min-w-[200px]">
      {loginIdentities.map((li) => (
        <div
          key={li.id}
          className="flex items-center justify-between gap-3 rounded-md border border-border/60 px-2 py-1.5"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium truncate">{li.email}</p>
            {li.name?.trim() ? (
              <p className="text-xs text-muted-foreground truncate">
                {li.name}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label
              htmlFor={`${idPrefix}acct-contact-${li.id}`}
              className="text-xs font-normal shrink-0"
            >
              {contactOn(li.id, li.is_accounts_contact)
                ? "Enabled"
                : "Disabled"}
            </Label>
            <Switch
              id={`${idPrefix}acct-contact-${li.id}`}
              checked={contactOn(li.id, li.is_accounts_contact)}
              disabled={disabled || (!deferPersist && mutation.isPending)}
              onCheckedChange={(checked) => {
                setContactOn(li.id, checked);
                if (onLoginIdentityChange) {
                  onLoginIdentityChange(li.id, checked);
                  return;
                }
                onCheckedChangeLi(li.id, checked);
              }}
              aria-label={`Accounts contact for ${li.email}`}
            />
          </div>
        </div>
      ))}
      {preLoginAccountsContact.map((row) => (
        <div
          key={row.registrant_account_id}
          className="flex items-center justify-between gap-3 rounded-md border border-border/60 border-dashed px-2 py-1.5"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium truncate">{row.email}</p>
            <p className="text-xs text-muted-foreground">
              Pending portal login
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label
              htmlFor={`${idPrefix}acct-contact-pre-${row.registrant_account_id}`}
              className="text-xs font-normal shrink-0"
            >
              {contactOn(
                preLoginKey(row.registrant_account_id),
                row.accounts_contact_before_login,
              )
                ? "Enabled"
                : "Disabled"}
            </Label>
            <Switch
              id={`${idPrefix}acct-contact-pre-${row.registrant_account_id}`}
              checked={contactOn(
                preLoginKey(row.registrant_account_id),
                row.accounts_contact_before_login,
              )}
              disabled={disabled || (!deferPersist && mutation.isPending)}
              onCheckedChange={(checked) => {
                setContactOn(preLoginKey(row.registrant_account_id), checked);
                if (onPreLoginChange) {
                  onPreLoginChange(row.registrant_account_id, checked);
                  return;
                }
                onCheckedChangePreLogin(row.registrant_account_id, checked);
              }}
              aria-label={`Accounts contact (before login) for ${row.email}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
